import { useState } from 'react';
import { isEmpty } from 'lodash/fp';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames/bind';
import { Button, ButtonVariant, ButtonEdge } from 'app/components/core/Button';
import { Icon, IconSize } from 'app/components/core/Icon';
import { FilterInput, InputSize } from 'app/components/composite/FilterInput';
import { Loading, LoadingSize } from 'app/components/core/Loading';
import { Truncate } from 'app/components/core/Truncate';
import { NoMatchFound } from './NoMatchFound.jsx';
import * as styles from './RoleAndWarehouseSelector.css';

const cx = classnames.bind(styles);

function startsWith(a, b) {
    return a.toLowerCase().startsWith(b.toLowerCase());
}

function ListItem({ name, isSelected, currentlyLoadingItem, icon, onClick }) {
    const { t } = useTranslation();
    const switchingToItemLabel = t('components:ListItemSelect.switchingTo', {
        itemInProgress: currentlyLoadingItem
    });
    const isLoadingItem = currentlyLoadingItem === name;
    const handleClick = () => {
        if (!isSelected) {
            onClick(name);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            handleClick();
        }
    };
    return (
        <Button
            as="li"
            edge={ButtonEdge.NONE}
            key={name}
            variant={ButtonVariant.GHOST}
            aria-selected={isSelected}
            onClick={handleClick}
            tabIndex={0}
            onKeyDown={handleKeyDown}
            title={name}>
            <Icon variant={icon} size={IconSize.EXTRA_SMALL} inline />
            <Truncate className={cx({ isLoadingItem })} maxWidth="220px">
                {isLoadingItem ? switchingToItemLabel : name}
            </Truncate>
            {isSelected && (
                <Icon
                    className={styles.checkmark}
                    variant="checkmark"
                    size={IconSize.EXTRA_SMALL}
                />
            )}
            {isLoadingItem && <Loading size={LoadingSize.EXTRA_SMALL} />}
        </Button>
    );
}

export function List({
    items,
    isLoading,
    searchPlaceholder,
    icon,
    onClick,
    currentlyLoadingItem,
    emptyStateMessage
}) {
    const [filter, setFilter] = useState('');
    const isFilterSet = filter !== '';
    const matchedItems = isFilterSet ? items.filter(({ name }) => startsWith(name, filter)) : items;
    const isLoadingItems = isLoading && !currentlyLoadingItem;

    const renderList = () => {
        if (isLoadingItems) {
            return <Loading className={styles.loading} size={LoadingSize.LARGE} />;
        }
        if (!isEmpty(matchedItems)) {
            return (
                <ul className={styles.list}>
                    {matchedItems.map(({ name, isSelected }) => (
                        <ListItem
                            key={name}
                            name={name}
                            isSelected={isSelected}
                            currentlyLoadingItem={currentlyLoadingItem}
                            icon={icon}
                            onClick={onClick}
                        />
                    ))}
                </ul>
            );
        }
        if (filter) {
            return <NoMatchFound filter={filter} icon={icon} message={emptyStateMessage} />;
        }
        return null;
    };
    return (
        <div className={styles.listContainer}>
            <FilterInput
                className={styles.search}
                size={InputSize.EXTRA_LARGE}
                onChange={(value) => {
                    setFilter(value);
                }}
                placeholder={searchPlaceholder}
                value={filter}
                data-testid="search"
            />
            {renderList()}
        </div>
    );
}
